import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, Search, X } from 'lucide-react';
import { useToast } from '../../ui/Toast';
import Button from '../../ui/Button';
import EmptyState, { CanvaNotConnectedEmpty, NoDesignsEmpty } from '../../ui/EmptyState';
import { DesignCardSkeleton } from '../../ui/SkeletonLoader';
import DesignGrid from '../dashboard/DesignGrid';
import FolderSection from '../dashboard/FolderSection';
import { useDesignBrowser } from '../../../hooks/useDesignBrowser';
import { COPY, noResultsTitle, resultsLine } from './playlistCopy';

const GRID_CLASSES = 'grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6';
const HEADING_ID = 'playlist-picker-heading';

function LoadingGrid() {
  return (
    <div data-testid="designs-loading-skeleton" className={GRID_CLASSES}>
      {Array.from({ length: 8 }, (_, i) => <DesignCardSkeleton key={i} />)}
    </div>
  );
}

/**
 * The Dashboard's browser (folders, breadcrumb, pagination, account-wide
 * search) with the design cards in add mode.
 */
export default function PlaylistPicker({
  canvaConnected,
  countsByDesignId,
  addDisabledReason,
  onAdd,
  searchInputRef,
  searchPulse,
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const browser = useDesignBrowser({
    enabled: canvaConnected,
    onSearchError: () => toast.error(COPY.searchFailed),
  });
  const {
    folderStack, currentFolderId, pageIndex, hasMore, searchQuery, setSearchQuery,
    debouncedSearchQuery, isSearchActive, isSearching, designs, isLoading, isDesignsLoading,
    enterFolder, navigateToBreadcrumb, goNext, goPrev, goFirst,
  } = browser;

  const pagination = !isSearchActive && (pageIndex > 0 || hasMore) ? (
    <div className="flex items-center justify-center gap-2" data-testid="pagination-controls">
      <Button onClick={goFirst} disabled={pageIndex === 0 || isDesignsLoading} variant="secondary" size="sm" aria-label="First page">
        « First
      </Button>
      <Button onClick={goPrev} disabled={pageIndex === 0 || isDesignsLoading} variant="secondary" size="sm" aria-label="Previous page">
        <ChevronLeft className="w-4 h-4 mr-1" /> Prev
      </Button>
      <span className="px-3 text-body-sm text-foreground-secondary">
        Page {pageIndex + 1}{hasMore ? '+' : ''}
      </span>
      <Button onClick={goNext} disabled={!hasMore || isDesignsLoading} variant="secondary" size="sm" aria-label="Next page">
        Next <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  ) : null;

  if (!canvaConnected) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="sr-only">{COPY.pickerHeading}</h2>
        <CanvaNotConnectedEmpty
          onAction={(
            <Button onClick={() => navigate('/settings?action=link-canva')} variant="primary" size="lg">
              Connect Canva Account
            </Button>
          )}
        />
      </section>
    );
  }

  const showNoResults = isSearchActive && !isSearching && designs.length === 0;
  const showNoDesigns = !isSearchActive && !isDesignsLoading && designs.length === 0;

  return (
    <section aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="text-heading text-gray-900 dark:text-gray-100 mb-3 md:sr-only">{COPY.pickerHeading}</h2>

      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={COPY.searchPlaceholder}
            aria-label={COPY.searchLabel}
            data-testid="design-search-input"
            className={`w-full pl-10 pr-9 py-2.5 squircle-sm bg-white/80 dark:bg-elevated-dark-2 border border-gray-200 dark:border-white/5 shadow-soft-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-body-sm [&::-webkit-search-cancel-button]:hidden ${
              searchPulse ? 'ring-2 ring-primary-500 animate-pulse-slow' : ''
            }`}
            style={searchPulse ? { animationIterationCount: 2 } : undefined}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label={COPY.clearSearch}
              data-testid="design-search-clear"
              className="absolute right-1 top-1/2 -translate-y-1/2 touch-target flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {isSearchActive && (
          <p className="mt-2 text-body-sm text-gray-600 dark:text-gray-400" data-testid="playlist-search-results-line" aria-live="polite">
            {resultsLine(debouncedSearchQuery, designs.length)}
          </p>
        )}
      </div>

      {!isSearchActive && folderStack.length > 1 && (
        <nav aria-label="Folder breadcrumb" className="flex items-center flex-wrap gap-1 mb-4 text-body-sm" data-testid="folder-breadcrumb">
          {folderStack.map((seg, idx) => {
            const isLast = idx === folderStack.length - 1;
            return (
              <React.Fragment key={`${seg.id}:${idx}`}>
                <button
                  type="button"
                  onClick={() => !isLast && navigateToBreadcrumb(idx)}
                  disabled={isLast}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md ${
                    isLast
                      ? 'text-foreground font-semibold cursor-default'
                      : 'text-foreground-secondary hover:text-foreground hover:bg-foreground/5'
                  }`}
                >
                  {idx === 0 && <Home className="w-3.5 h-3.5" />}
                  <span className="truncate max-w-[14rem]">{seg.name}</span>
                </button>
                {!isLast && <ChevronRight className="w-3.5 h-3.5 text-foreground-tertiary" />}
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {!isSearchActive && (
        <FolderSection folderId={currentFolderId} onEnterFolder={enterFolder} />
      )}

      {showNoResults && (
        <EmptyState
          icon={(
            <svg className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
          title={noResultsTitle(debouncedSearchQuery)}
          description={COPY.noResultsBody}
          action={(
            <Button onClick={() => setSearchQuery('')} variant="secondary" size="lg">
              {COPY.clearSearch}
            </Button>
          )}
        />
      )}

      {showNoDesigns && (
        <NoDesignsEmpty
          onAction={(
            <Button onClick={() => window.open('https://www.canva.com', '_blank', 'noopener')} variant="primary" size="lg">
              Open Canva
            </Button>
          )}
        />
      )}

      {pagination && <div className="mb-4">{pagination}</div>}

      {isLoading && designs.length === 0 ? (
        <LoadingGrid />
      ) : designs.length > 0 && (
        <DesignGrid
          designs={designs}
          mode="add"
          playlistCounts={countsByDesignId}
          onAdd={onAdd}
          addDisabledReason={addDisabledReason}
          className={GRID_CLASSES}
        />
      )}

      {pagination && <div className="mt-6">{pagination}</div>}
    </section>
  );
}
